import { useEffect, useState } from "react";
import { useModalStore } from "../../stores/modalStore.js";
import { useChannelStore } from "../../stores/channelStore.js";

const SHORTCUT_GROUPS = [
  {
    label: 'Navigation',
    color: '#6366f1',
    shortcuts: [
      { keys: ['Ctrl', 'K'],     label: 'Quick Switcher' },
      { keys: ['Alt', '↑'],      label: 'Previous channel' },
      { keys: ['Alt', '↓'],      label: 'Next channel' },
      { keys: ['Ctrl', ','],     label: 'Open Settings' },
      { keys: ['Esc'],           label: 'Close modal / cancel' },
    ],
  },
  {
    label: 'Messaging',
    color: '#22c55e',
    shortcuts: [
      { keys: ['Enter'],         label: 'Send message' },
      { keys: ['Shift', 'Enter'],label: 'New line' },
      { keys: ['↑'],             label: 'Edit last message' },
      { keys: ['T'],             label: 'Focus message input' },
      { keys: ['Ctrl', 'F'],     label: 'Search messages' },
    ],
  },
  {
    label: 'UI & Features',
    color: '#06b6d4',
    shortcuts: [
      { keys: ['?'],             label: 'Show keyboard shortcuts' },
      { keys: ['Ctrl', '/'],     label: 'Show keyboard shortcuts' },
    ],
  },
];

const ALL_SHORTCUTS = SHORTCUT_GROUPS.flatMap(g => g.shortcuts);

function Kbd({ children }) {
  return (
    <kbd style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 8px',
      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
      borderBottom: '2px solid rgba(255,255,255,0.12)',
      borderRadius: 5, fontSize: 11, fontFamily: 'inherit',
      color: 'rgba(255,255,255,0.85)', lineHeight: '17px', fontWeight: 600,
    }}>
      {children}
    </kbd>
  );
}

function ShortcutsOverlay({ onClose }) {
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '?' && document.activeElement?.tagName !== 'INPUT') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const q = search.toLowerCase();
  const filteredGroups = q
    ? [{ label: 'Results', color: '#6366f1', shortcuts: ALL_SHORTCUTS.filter(s => s.label.toLowerCase().includes(q) || s.keys.join(' ').toLowerCase().includes(q)) }]
    : SHORTCUT_GROUPS;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#0f1117', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 14, width: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.8)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label="Keyboard shortcuts"
      >
        {/* Header */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>⌨️ Keyboard Shortcuts</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search shortcuts…"
            autoFocus
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        {/* Groups */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 22px 20px' }}>
          {filteredGroups.map(group => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.7px', color: group.color, marginBottom: 8 }}>
                {group.label}
              </div>
              {group.shortcuts.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < group.shortcuts.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{s.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {s.keys.map((k, j) => (
                      <span key={j} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {j > 0 && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', fontWeight: 700 }}>+</span>}
                        <Kbd>{k}</Kbd>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {filteredGroups[0]?.shortcuts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No shortcuts match "{search}"</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 22px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>Press <Kbd>?</Kbd> or <Kbd>Esc</Kbd> to close</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>{ALL_SHORTCUTS.length} shortcuts</span>
        </div>
      </div>
    </div>
  );
}

export default function KeyboardShortcuts() {
  const onOpen            = useModalStore(s => s.onOpen);
  const channels          = useChannelStore(s => s.channels);
  const currentChannelId  = useChannelStore(s => s.currentChannelId);
  const setCurrentChannel = useChannelStore(s => s.setCurrentChannel);
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.contentEditable === 'true';

      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); onOpen('quickSwitcher'); return; }
      if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const textChannels = channels.filter(c => c.type !== 'voice' && c.type !== 'stage');
        const idx = textChannels.findIndex(c => c.id === currentChannelId);
        if (idx === -1) return;
        const next = e.key === 'ArrowUp' ? idx - 1 : idx + 1;
        if (next >= 0 && next < textChannels.length) setCurrentChannel(textChannels[next].id);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '/') { e.preventDefault(); setShowOverlay(v => !v); return; }

      if (inInput) return;

      if (e.key === 't' || e.key === 'T') { e.preventDefault(); document.querySelector('.message-input')?.focus(); return; }
      if (e.key === '?') { e.preventDefault(); setShowOverlay(v => !v); return; }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onOpen, channels, currentChannelId, setCurrentChannel]);

  return showOverlay ? <ShortcutsOverlay onClose={() => setShowOverlay(false)} /> : null;
}
